import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../services/api";
import { showToast } from "../helper/notifications";
import { Icon } from "@iconify/react";
import { GoogleMap, MarkerF, Autocomplete } from "@react-google-maps/api";
import { useGoogleMaps } from "../context/GoogleMapsContext"; 
import PageHeader from "../components/common/PageHeader";
import LocationHistoryModal from "../components/LocationHistoryModal";

const DEFAULT_CENTER = { lat: 47.8238, lng: 13.0288 };

const TABS = [
  { id: "Identity", icon: "solar:shop-2-linear" },
  { id: "Address", icon: "solar:map-point-linear" },
  { id: "Social & Web", icon: "solar:global-linear" },
  { id: "Opening Hours", icon: "solar:clock-circle-linear" },
  { id: "Parking", icon: "carbon:car" }, 
  { id: "Accessibility", icon: "carbon:accessibility" }, 
  { id: "Inclusivity", icon: "solar:heart-angle-linear" },
  { id: "Media & Files", icon: "solar:gallery-wide-linear" },
  { id: "Internal IDs", icon: "solar:database-linear" },
];

// ==========================================
// MAIN FORM PAGE
// ==========================================
const LocationsMainFormPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isLoaded } = useGoogleMaps();
  
  const mapRef = useRef(null);
  const autocompleteRef = useRef(null);

  const [parentAreas, setParentAreas] = useState([]);
  const [activeTab, setActiveTab] = useState("Identity");
  const [loading, setLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);

  // History Modal State
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);

  const [initialData, setInitialData] = useState(null);

  const [formData, setFormData] = useState({
    location_name: "",
    internal_id: "",
    lat: DEFAULT_CENTER.lat,
    lng: DEFAULT_CENTER.lng,
    street_address: "",
    house_number: "",
    postal_code: "",
    city: "",
    district: "",
    province: "",
    region: "",
    state: "",
    country: "",
    building_levels: "",
    main_entrance_level: "",
    description: "",
    opening_date: "",
    primary_category_id: "",
    additional_category: "",
    phone_country_code: "",
    phone_number: "",
    email: "",
    website_url: "",
    youtube_link: "",
    twitter_link: "",
    facebook_link: "",
    instagram_link: "",
    linkedin_link: "",
    tiktok_link: "",
    whatsapp_chat: "",
    text_message_chat: "",
    hours_monday: "", hours_tuesday: "", hours_wednesday: "", hours_thursday: "", 
    hours_friday: "", hours_saturday: "", hours_sunday: "",
    lgbtq_friendly: 0, on_site_parking: 0, free_parking_lot: 0, paid_parking_lot: 0,
    paid_street_parking: 0, appointment_required: 0, assistive_hearing_loop: 0,
    gender_neutral_toilets: 0, transgender_safe_space: 0, free_multi_storey_car_park: 0,
    paid_multi_storey_car_park: 0, wheelchair_accessible_toilet: 0, 
    free_of_charge_street_parking: 0, wheelchair_accessible_seating: 0,
    wheelchair_accessible_car_park: 0, wheelchair_accessible_entrance: 0,
    google_link: "", google_place_id: "", referring_top_location: "",
    media_floorplan: "", media_photos: "", media_videos: "", media_logos: "",
    geometrics_outline: "", top_location_id: "",
    version_number: 1
  });

  const loadLocationData = useCallback(async () => {
    if (!id) return;
    try {
      const res = await api.get(`/main-locations/${id}`);
      const d = res.data.data;
      
      const loadedData = {
        ...d,
        lat: parseFloat(d.lat) || DEFAULT_CENTER.lat,
        lng: parseFloat(d.lng) || DEFAULT_CENTER.lng,
        lgbtq_friendly: d.lgbtq_friendly ? 1 : 0,
        on_site_parking: d.on_site_parking ? 1 : 0,
        free_parking_lot: d.free_parking_lot ? 1 : 0,
        paid_parking_lot: d.paid_parking_lot ? 1 : 0,
        paid_street_parking: d.paid_street_parking ? 1 : 0,
        appointment_required: d.appointment_required ? 1 : 0,
        assistive_hearing_loop: d.assistive_hearing_loop ? 1 : 0,
        gender_neutral_toilets: d.gender_neutral_toilets ? 1 : 0,
        transgender_safe_space: d.transgender_safe_space ? 1 : 0,
        free_multi_storey_car_park: d.free_multi_storey_car_park ? 1 : 0,
        paid_multi_storey_car_park: d.paid_multi_storey_car_park ? 1 : 0,
        wheelchair_accessible_toilet: d.wheelchair_accessible_toilet ? 1 : 0,
        free_of_charge_street_parking: d.free_of_charge_street_parking ? 1 : 0,
        wheelchair_accessible_seating: d.wheelchair_accessible_seating ? 1 : 0,
        wheelchair_accessible_car_park: d.wheelchair_accessible_car_park ? 1 : 0,
        wheelchair_accessible_entrance: d.wheelchair_accessible_entrance ? 1 : 0,
        opening_date: d.opening_date ? d.opening_date.split('T')[0] : "",
        version_number: d.version_number || 1
      };

      setFormData(loadedData);
      setInitialData(loadedData);
    } catch (err) {
      showToast("error", "Failed to load location details");
    }
  }, [id]);

  useEffect(() => {
    const init = async () => {
      try {
        const areaRes = await api.get("/top-locations", { params: { limit: 1000 } });
        setParentAreas(areaRes.data?.data || []);
        if (id) await loadLocationData();
      } catch (err) { 
        showToast("error", "Initialization failed");
      } finally { 
        setLoading(false); 
      }
    };
    init();
  }, [id, loadLocationData]);

  const hasFormChanged = () => {
    if (!initialData) return true; 
    const safeStr = (val) => (val === null || val === undefined ? "" : String(val).trim());
    
    return Object.keys(formData).some((key) => {
      if (["updated_at", "created_at", "version_number"].includes(key)) return false;
      return safeStr(formData[key]) !== safeStr(initialData[key]);
    });
  };

  const handleOpenHistory = async () => {
    setShowHistoryModal(true);
    setIsHistoryLoading(true);

    try {
      const response = await api.get(`/main-locations/${id}/history`);
      setHistoryData(response.data.data || []);
    } catch (error) {
      console.error("Error fetching version history:", error);
      showToast("error", "Failed to fetch version history");
    } finally {
      setIsHistoryLoading(false);
    }
  };

  const onMapLoad = useCallback((map) => { mapRef.current = map; }, []);

  const onPlaceChanged = () => {
    const place = autocompleteRef.current?.getPlace();
    if (!place?.geometry) return;
    const newPos = { lat: place.geometry.location.lat(), lng: place.geometry.location.lng() };
    
    setFormData(p => ({ 
      ...p, 
      lat: newPos.lat, 
      lng: newPos.lng, 
      street_address: place.name || p.street_address,
      city: place.address_components?.find(c => c.types.includes("locality"))?.long_name || p.city,
      country: place.address_components?.find(c => c.types.includes("country"))?.long_name || p.country
    }));
    mapRef.current?.panTo(newPos);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : value 
    }));
  };

  // In LocationsMainFormPage.jsx inside handleSubmit
const handleSubmit = async (e) => {
  e.preventDefault();

  if (id && !hasFormChanged()) {
    showToast("info", "No changes detected in any field.");
    return;
  }

  setSaveLoading(true); 

  try {
    // Retrieve logged in user info (from localStorage, auth context, or redux)
    const currentUser = JSON.parse(localStorage.getItem("user") || "{}");

    const payload = { 
      ...formData,
      user_id: currentUser.id || null // Pass user ID explicitly
    };
    
    if (id) {
      await api.put(`/main-locations/${id}`, payload);
      showToast("success", "Location profile saved successfully");
      await loadLocationData(); 
    } else {
      const response = await api.post("/main-locations", payload);
      showToast("success", "Location created successfully");
      const newLocationId = response.data.location_id; 
      navigate(`/locations-main/${newLocationId}/edit`); 
    }
  } catch (error) { 
    console.error("Save error:", error);
    showToast("error", "Failed to save record");
  } finally {
    setSaveLoading(false); 
  }
};

const handleRestoreVersion = async (versionNumber) => {
  try {
    console.log('restore start');
    const currentUser = JSON.parse(localStorage.getItem("user") || "{}");

    await api.post(`/main-locations/${id}/restore/${versionNumber}`, {
      user_id: currentUser.id
    });

    showToast("success", `Restored version ${versionNumber} successfully!`);
    
    // Refresh main form values & modal history list
    await loadLocationData(); 
    setShowHistoryModal(false);
  } catch (err) {
    console.error("Restore failed:", err);
    showToast("error", "Failed to restore historical version.");
  }
};

  if (loading) return (
    <div className="d-flex justify-content-center align-items-center py-80 bg-base radius-12 shadow-sm">
      <Icon icon="line-md:loading-twotone-loop" className="text-primary-600 text-4xl" />
    </div>
  );

  return (
    <>
      {/* CLEAN BACK BUTTON */}
      <button
        type="button"
        onClick={() => navigate("/locations-main")}
        className="btn btn-link p-0 text-secondary-light text-decoration-none d-inline-flex align-items-center gap-2 mb-16 hover-text-primary-600 transition-all"
      >
        <Icon icon="solar:arrow-left-linear" className="text-xl" />
        <span className="fw-medium text-sm">Back to Locations</span>
      </button>

      {/* HEADER SECTION WITH HISTORY BUTTON */}
      <div className="d-flex align-items-center justify-content-between mb-24">
        <PageHeader title={id ? "Edit Location Profile" : "Add New Location"} />
        
        {id && (
          <button
            type="button"
            onClick={handleOpenHistory}
            className="btn btn-outline-primary d-inline-flex align-items-center gap-2 px-16 py-10 radius-8 fw-semibold"
          >
            <Icon icon="solar:history-linear" className="text-xl" />
            <span className="text-sm">View History</span>
          </button>
        )}
      </div>

      <form onSubmit={handleSubmit}>
        <div className="card radius-12 border-0 shadow-none bg-base overflow-hidden">
          <div className="d-flex flex-column flex-lg-row">
            
            {/* NAVIGATION SIDEBAR WITH ICONS */}
            <div className="bg-neutral-50 border-end" style={{ minWidth: "260px" }}>
              <div className="p-16">
                <div className="d-flex flex-column gap-1">
                  {TABS.map((tab) => (
                    <button key={tab.id} type="button" onClick={() => setActiveTab(tab.id)}
                      className={`d-flex align-items-center gap-3 px-16 py-12 radius-8 border-0 transition-all text-start w-100 ${
                        activeTab === tab.id ? "bg-primary-600 text-white shadow-sm fw-bold" : "bg-transparent text-secondary-light hover-bg-neutral-200"
                      }`}>
                      <Icon icon={tab.icon} className={`text-xl ${activeTab === tab.id ? "text-white" : "text-primary-600"}`} />
                      <span className="text-sm">{tab.id}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* FORM CONTENT */}
            <div className="flex-grow-1 p-32 p-lg-40" style={{ minHeight: "75vh" }}>
              <h5 className="fw-bold text-primary-light mb-32 border-bottom pb-16">{activeTab}</h5>

              {activeTab === "Identity" && (
                <div className="row g-4">
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Location Name (Public)</label>
                    <input className="form-control" name="location_name" value={formData.location_name} onChange={handleChange} required />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Parent Top Location</label>
                    <select className="form-select" name="top_location_id" value={formData.top_location_id} onChange={handleChange}>
                      <option value="">Select Parent Area</option>
                      {parentAreas?.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Category</label>
                    <input className="form-control" name="primary_category_id" placeholder="Primary Category ID" value={formData.primary_category_id} onChange={handleChange} />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Opening Date</label>
                    <input type="date" className="form-control" name="opening_date" value={formData.opening_date} onChange={handleChange} />
                  </div>
                  <div className="col-12">
                    <label className="form-label fw-semibold">Public Description</label>
                    <textarea className="form-control" name="description" rows="4" value={formData.description} onChange={handleChange}></textarea>
                  </div>
                </div>
              )}

              {activeTab === "Address" && (
                <div className="row g-4">
                  <div className="col-lg-5">
                    {isLoaded && (
                      <Autocomplete onLoad={a => (autocompleteRef.current = a)} onPlaceChanged={onPlaceChanged}>
                        <div className="icon-field mb-24">
                          <span className="icon"><Icon icon="solar:magnifer-linear" /></span>
                          <input className="form-control radius-8 ps-40" placeholder="Search for address..." />
                        </div>
                      </Autocomplete>
                    )}
                    <div className="row g-3">
                      <div className="col-8"><label className="form-label fw-semibold text-sm">Street</label><input className="form-control form-control-sm" name="street_address" value={formData.street_address} onChange={handleChange} /></div>
                      <div className="col-4"><label className="form-label fw-semibold text-sm">Number</label><input className="form-control form-control-sm" name="house_number" value={formData.house_number} onChange={handleChange} /></div>
                      <div className="col-6"><label className="form-label fw-semibold text-sm">City</label><input className="form-control form-control-sm" name="city" value={formData.city} onChange={handleChange} /></div>
                      <div className="col-6"><label className="form-label fw-semibold text-sm">Zip</label><input className="form-control form-control-sm" name="postal_code" value={formData.postal_code} onChange={handleChange} /></div>
                      <div className="col-12"><label className="form-label fw-semibold text-sm">Country</label><input className="form-control form-control-sm" name="country" value={formData.country} onChange={handleChange} /></div>
                    </div>
                    <div className="row g-3 mt-12">
                       <div className="col-6"><label className="text-xs text-secondary-light">Latitude</label><input className="form-control form-control-sm bg-neutral-50" readOnly value={formData.lat} /></div>
                       <div className="col-6"><label className="text-xs text-secondary-light">Longitude</label><input className="form-control form-control-sm bg-neutral-50" readOnly value={formData.lng} /></div>
                    </div>
                  </div>
                  <div className="col-lg-7">
                    <div className="border radius-12 overflow-hidden shadow-sm h-100" style={{ minHeight: "400px" }}>
                      {isLoaded ? (
                        <GoogleMap onLoad={onMapLoad} mapContainerStyle={{ width: "100%", height: "100%" }} center={{ lat: Number(formData.lat), lng: Number(formData.lng) }} zoom={16}>
                          <MarkerF position={{ lat: Number(formData.lat), lng: Number(formData.lng) }} draggable onDragEnd={e => setFormData(p => ({ ...p, lat: e.latLng.lat(), lng: e.latLng.lng() }))} />
                        </GoogleMap>
                      ) : <div className="d-flex align-items-center justify-content-center h-100 bg-neutral-100 text-secondary-light">Loading Maps...</div>}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "Social & Web" && (
                <div className="row g-4">
                   {['website_url', 'email', 'phone_number', 'facebook_link', 'instagram_link', 'twitter_link', 'linkedin_link', 'whatsapp_chat', 'tiktok_link', 'youtube_link'].map(key => (
                     <div className="col-md-6" key={key}>
                       <label className="form-label fw-semibold text-capitalize text-sm">{key.replace(/_url|_link|_chat/g, '').replace('_', ' ')}</label>
                       <input className="form-control radius-8" name={key} value={formData[key] || ""} onChange={handleChange} placeholder={`Enter ${key.replace('_', ' ')}`} />
                     </div>
                   ))}
                </div>
              )}

              {activeTab === "Opening Hours" && (
                <div className="bg-neutral-50 p-24 radius-12 border">
                   <div className="row g-3">
                     {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(day => (
                       <div className="col-md-6 d-flex align-items-center gap-3 bg-white p-12 radius-8 border shadow-sm" key={day}>
                         <label className="fw-bold text-sm text-primary-600 mb-0 text-capitalize" style={{ width: "100px" }}>{day}</label>
                         <input className="form-control border-0 bg-neutral-50 text-sm" name={`hours_${day}`} placeholder="e.g. 09:00 - 18:00" value={formData[`hours_${day}`] || ""} onChange={handleChange} />
                       </div>
                     ))}
                   </div>
                </div>
              )}

              {["Parking", "Accessibility", "Inclusivity"].includes(activeTab) && (
                <div className="row g-3">
                  {Object.keys(formData).filter(k => 
                    (activeTab === "Parking" && k.includes('parking')) || 
                    (activeTab === "Accessibility" && (k.includes('wheelchair') || k.includes('hearing'))) ||
                    (activeTab === "Inclusivity" && (k.includes('gender') || k.includes('lgbtq') || k.includes('transgender')))
                  ).map(key => (
                    <div className="col-md-6" key={key}>
                      <div className="d-flex align-items-center justify-content-between p-16 border radius-12 bg-neutral-50 hover-bg-white transition-all shadow-sm">
                        <span className="fw-semibold text-secondary-light text-sm text-capitalize">{key.replace(/_/g, ' ')}</span>
                        <div className="form-check form-switch p-0 m-0">
                          <input className="form-check-input ms-0 cursor-pointer" type="checkbox" name={key} checked={formData[key] === 1} onChange={handleChange} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {activeTab === "Media & Files" && (
                <div className="row g-4">
                  <div className="col-md-6"><label className="form-label fw-semibold">Logo URL</label><input className="form-control" name="media_logos" value={formData.media_logos} onChange={handleChange} /></div>
                  <div className="col-md-6"><label className="form-label fw-semibold">Floorplan URL</label><input className="form-control" name="media_floorplan" value={formData.media_floorplan} onChange={handleChange} /></div>
                  <div className="col-12"><label className="form-label fw-semibold">Photo Gallery (JSON or Links)</label><textarea className="form-control" name="media_photos" rows="3" value={formData.media_photos} onChange={handleChange} /></div>
                  <div className="col-12"><label className="form-label fw-semibold">Boundary Geometry (GeoJSON)</label><textarea className="form-control bg-neutral-50 text-xs" name="geometrics_outline" rows="2" value={formData.geometrics_outline} onChange={handleChange} /></div>
                </div>
              )}

              {activeTab === "Internal IDs" && (
                <div className="row g-4">
                  <div className="col-md-6"><label className="form-label text-xs fw-bold">INTERNAL SYSTEM ID</label><input className="form-control bg-neutral-100" name="internal_id" value={formData.internal_id} onChange={handleChange} /></div>
                  <div className="col-md-6"><label className="form-label text-xs fw-bold">GOOGLE PLACE ID</label><input className="form-control bg-neutral-100" name="google_place_id" value={formData.google_place_id} onChange={handleChange} /></div>
                  <div className="col-12"><label className="form-label text-xs fw-bold">GOOGLE MAPS URL</label><input className="form-control bg-neutral-100" name="google_link" value={formData.google_link} onChange={handleChange} /></div>
                </div>
              )}
            </div>
          </div>

          {/* FORM ACTIONS */}
          <div className="card-footer bg-base border-top d-flex justify-content-end gap-3 p-24">
            <button type="button" className="btn btn-outline-danger px-32 py-11 radius-8 fw-semibold" onClick={() => navigate("/locations-main")}>Discard Changes</button>
            <button type="submit" disabled={saveLoading} className="btn btn-primary-600 px-32 py-11 radius-8 fw-semibold d-flex align-items-center gap-2">
              {saveLoading ? <Icon icon="line-md:loading-twotone-loop" /> : <Icon icon="solar:check-read-linear" />}
              {id ? "Update Profile" : "Create New Location"}
            </button>
          </div>
        </div>
      </form>

      {/* VERSION HISTORY MODAL */}
      <LocationHistoryModal
        isOpen={showHistoryModal}
        onClose={() => setShowHistoryModal(false)}
        historyData={historyData}
        isLoading={isHistoryLoading}
        onRestore={handleRestoreVersion}
      />
    </>
  );
};

export default LocationsMainFormPage;